import { observer } from "mobx-react";
import * as React from "react";
import { PasswordStrengthBarProps } from "react-password-strength-bar";
import styled from "styled-components";

import Style from "@client/utils/Style";
import { InputField as InputFieldType, Toggle } from "@typings/InputField";

import DatetimePicker from "../DatetimePicker/DatetimePicker";
import Dropdown from "../Dropdown";
import DropdownAutocomplete from "../DropdownAutocomplete";
import InfoIcon from "../InfoIcon";
import Stepper from "../Stepper";
import StepperDropdownToggle from "../StepperDropdownToggle";
import Switch from "../Switch";
import TextInput from "../TextInput";
import asteriskImage from "./images/asterisk.svg";

const Wrapper = styled.div`
  position: relative;
`;
const HighlightLabel = styled.div`
  position: absolute;
  bottom: -13px;
  font-size: 10px;
  right: 0;
  color: ${Style.palette.alert};
`;
export const Label = styled.div`
  font-size: 10px;
  font-weight: ${Style.fontWeights.medium};
  color: ${Style.palette.grayscale[3]};
  text-transform: uppercase;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
`;
export const LabelText = styled.span`
  margin-right: 8px;
`;
const Asterisk = styled.div<{ marginLeft?: string }>`
  ${Style.exactSize("12px")}
  display: inline-block;
  background: url("${asteriskImage}") center no-repeat;
  margin-bottom: 2px;
  margin-left: ${props => props.marginLeft || "0px"};
  opacity: 0.8;
`;

export type Props = {
  field: InputFieldType;
  onChange: (value: any, toggle?: Toggle) => void;
  onEnumNewValueChange?: (value: string) => void;
  disabled?: boolean;
  highlight?: boolean;
  highlightLabel?: string;
  enumItemRender?: ((item: any) => React.ReactNode) | null;
  selectedItemComponent?: React.ReactNode;
  isValidDate?: (field: InputFieldType, value: Date) => boolean;
  showPasswordStrength?: boolean;
  onPasswordStrengthChangeScore?: PasswordStrengthBarProps["onChangeScore"];
};
@observer
class Field extends React.Component<Props> {
  renderDateInput() {
    return (
      <DatetimePicker
        width={224}
        timezone="utc"
        onChange={date => {
          if (this.props.onChange) {
            this.props.onChange(date);
          }
        }}
        // value={moment.utc(moment(new Date()).format('YYYY-MM-DD')).toDate()}
        value={this.props.field.value || new Date()}
        dispatchChangeContinously
        disabled={!!this.props.disabled}
        highlight={!!this.props.highlight}
        isValidDate={date =>
          this.props.isValidDate
            ? this.props.isValidDate(this.props.field, date)
            : true
        }
      />
    );
  }

  renderSwitch() {
    return (
      <Switch
        disabled={this.props.disabled}
        checked={this.props.field.value}
        width={`${Style.inputSizes.large.width}px`}
        onChange={checked => {
          if (this.props.onChange) {
            this.props.onChange(checked);
          }
        }}
      />
    );
  }

  renderTextInput() {
    return (
      <TextInput
        large
        highlight={!!this.props.highlight}
        value={this.props.field.value || ""}
        onChange={value => {
          if (this.props.onChange) {
            this.props.onChange(value);
          }
        }}
        autoComplete={this.props.field.password ? "new-password" : ""}
        type={this.props.field.password ? "password" : "text"}
        showPasswordStrength={this.props.showPasswordStrength}
        onPasswordStrengthChangeScore={this.props.onPasswordStrengthChangeScore}
        placeholder={this.props.field.label}
        disabled={!!this.props.disabled}
      />
    );
  }

  renderIntInput() {
    return (
      <Stepper
        large
        highlight={!!this.props.highlight}
        value={this.props.field.value || 0}
        onChange={value => {
          if (this.props.onChange) {
            this.props.onChange(value);
          }
        }}
        placeholder={this.props.field.label}
        disabled={!!this.props.disabled}
      />
    );
  }

  renderIntegerToggle() {
    const toggles = this.props.field.toggles;
    if (!toggles) {
      return null;
    }
    const selectedToggle =
      toggles.find(t => t.value === this.props.field.selectedToggle) ||
      toggles[0];

    return (
      <StepperDropdownToggle
        items={toggles}
        selectedItem={selectedToggle}
        onItemChange={item => {
          const toggle =
            toggles!.find(t => t.value === item.value) || toggles![0];
          this.props.onChange(toggle.associatedValue || 0, item);
        }}
        inputValue={selectedToggle.associatedValue || 0}
        onInputChange={value => {
          this.props.onChange(value, selectedToggle);
        }}
        highlight={this.props.highlight}
        disabled={this.props.disabled}
      />
    );
  }

  renderEnumDropdown() {
    const enums = this.props.field.enum;
    if (!enums) {
      return null;
    }
    const selectedItem = enums.find(i => i.value === this.props.field.value);

    if (enums.length < 5 || this.props.field.noAutocomplete) {
      return (
        <Dropdown
          large
          allowNew={this.props.field.enumAllowNew}
          newValue={this.props.field.enumNewValue}
          onNewValueChange={val => {
            if (this.props.onEnumNewValueChange) {
              this.props.onEnumNewValueChange(val);
            }
          }}
          selectedItem={selectedItem}
          noSelectionMessage={this.props.field.noSelectionMessage}
          noItemsMessage={this.props.field.noItemsMessage}
          items={enums}
          onChange={item => {
            if (this.props.onChange) {
              this.props.onChange(item.value);
            }
          }}
          disabled={this.props.disabled}
          highlight={this.props.highlight}
          itemRenderer={this.props.enumItemRender || null}
          selectedItemComponent={this.props.selectedItemComponent}
        />
      );
    }

    return (
      <DropdownAutocomplete
        width={Style.inputSizes.large.width}
        items={enums}
        disabled={this.props.disabled}
        selectedItem={selectedItem}
        highlight={this.props.highlight}
        onChange={item => {
          if (this.props.onChange) {
            this.props.onChange(item.value);
          }
        }}
      />
    );
  }

  renderInput() {
    switch (this.props.field.type) {
      case "boolean":
        return this.renderSwitch();
      case "string":
        if (this.props.field.enum) {
          return this.renderEnumDropdown();
        }
        return this.renderTextInput();
      case "integer":
        return this.renderIntInput();
      case "integer-toggle":
        return this.renderIntegerToggle();
      case "date":
        return this.renderDateInput();
      default:
        return null;
    }
  }

  renderLabel() {
    let infoIcon = null;
    if (this.props.field.description) {
      infoIcon = (
        <InfoIcon
          text={this.props.field.description}
          marginLeft={-4}
          marginBottom={0}
        />
      );
    }

    return (
      <Label>
        <LabelText>{this.props.field.label}</LabelText>
        {infoIcon}
        {this.props.field.required ? (
          <Asterisk
            marginLeft={this.props.field.description ? "4px" : "-4px"}
          />
        ) : null}
      </Label>
    );
  }

  render() {
    return (
      <Wrapper>
        {this.renderLabel()}
        {this.renderInput()}
        {this.props.highlight ? (
          <HighlightLabel>
            {this.props.highlightLabel !== undefined
              ? this.props.highlightLabel
              : "Required field"}
          </HighlightLabel>
        ) : null}
      </Wrapper>
    );
  }
}

export default Field;
